import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/solid";
import _get from "lodash/get";
import { format } from "date-fns";
import { Formik, Field } from "formik";

import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import Tabs from "components/lib/Shared/Tabs";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import GeneralInformation from "components/Profiles/GeneralInformation";
import ResidentialDetails from "components/Profiles/ResidentialDetails";
import RequestConfirmationModal from "components/lib/Shared/CompanyDashboard/RequestConfirmationModal";
import { getDocumentData } from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import { getFullName } from "utils";
import Checkbox from "components/lib/Shared/Checkbox";
import OnlyOfficeDocumentEditor from "components/lib/Shared/OnlyOfficeDocumentEditor";
import {
  EyeIcon,
  EyeOffIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

const MODAL_NAME = "initiate_request_confirmation_modal_company_dashboard";
const DOCS_EXTENSIONS = [
  "vnd.openxmlformats-officedocument.wordprocessingml.document",
  "docx",
];
const PDF_EXTENSIONS = ["pdf"];

function RecentChangesSubRow({ visibleColumns, row, initialRows }) {
  const currentUser = useSelector((state) => state.userSlice);
  const [tabs] = useState([
    {
      id: "generalInformation",
      label: "General information",
    },
    {
      id: "residentialDetails",
      label: "Address details",
    },
    {
      id: "uploadDocuments",
      label: "File Uploads",
    },
    {
      id: "changedDocuments",
      label: "Change Documents",
    },
    // {
    //   id: "directorApprovalDocuments",
    //   label: "Approval Documents (Director)",
    // },
  ]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [toggleViewDoc, setToggleViewDoc] = useState(false);

  const { initiatedTransactions, getInitiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const goToPrevStep = () => {
    if (selectedTabIndex === 0 && handleBackBtn) handleBackBtn();
    else setSelectedTabIndex(selectedTabIndex - 1);
  };

  const goToNextStep = () => {
    setSelectedTabIndex(selectedTabIndex + 1);
  };

  const DocViewer = (documentOf) => {
    const documentUrlType =
      documentOf === "directorApprovalDocuments"
        ? row.original.uploadDirectorApprovalDocs
        : row.original.uploadChangeDocuments;

    const documentTitleName =
      documentOf === "directorApprovalDocuments"
        ? "Board Resolutions"
        : "Consent To Act";

    const isDoc = DOCS_EXTENSIONS.includes(
      documentUrlType.fileName.split(".")[1]
    );
    const isPDF = PDF_EXTENSIONS.includes(
      documentUrlType.fileName.split(".")[1]
    );

    if (isPDF) {
      return (
        <OnlyOfficeDocumentEditor
          key={documentTitleName}
          userFullName={getFullName(currentUser.names)}
          userId={currentUser._id}
          companyId={row.original.companyId}
          id={Math.random().toString(36).substring(2, 18)}
          url={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${documentUrlType.fileName}`}
          fileType="pdf"
          title={documentTitleName}
          mode="view"
          showFullScreenToggle={true}
          permissions={{
            chat: false,
            comment: false,
          }}
          containerHeight={"700px"}
        />
      );
    } else if (isDoc) {
      return (
        <>
          <OnlyOfficeDocumentEditor
            key={documentTitleName}
            userFullName={getFullName(currentUser.names)}
            title={documentTitleName}
            mode="view"
            userId={currentUser._id}
            companyId={row.original.companyId}
            url={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${documentUrlType.fileName}`}
            editorKey={`${documentUrlType.fileName}`}
            containerHeight="700px"
          />
        </>
      );
    }
  };
  useEffect(() => {
    const tempSelectedTab = tabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [tabs, selectedTabIndex]);

  if (getInitiatedTransactions.status === "loading") {
    return (
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className="px-9">
          <div className="flex justify-center mx-auto m-2">
            <SpinnerIcon className="text-gray-400" />
          </div>
        </td>
      </tr>
    );
  }

  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = tabs.length - 1 === selectedTabIndex;

  let dateOfBirth = _get(row.original.identificationDetails, "dateOfBirth");
  if (dateOfBirth) dateOfBirth = format(new Date(dateOfBirth), "yyyy/MM/dd");
  else dateOfBirth = "N/A";

  const handleConfirmDetails = () => {
    row.toggleRowExpanded();
    const nextRow = initialRows?.[row.index + 1] || null;
    if (nextRow) nextRow?.getToggleRowExpandedProps()?.onClick();
  };

  const toggleDoc = () => {
    setToggleViewDoc(!toggleViewDoc);
  };

  return (
    <tr className="bg-[#EDF0F5]">
      <RequestConfirmationModal modalName={MODAL_NAME} />
      <td colSpan={visibleColumns.length} className={`px-9`}>
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
        />
        {selectedTab?.id === "generalInformation" && (
          <GeneralInformation
            official={row.original}
            isCurrentUserRow={true}
            viewRestrictedData={true}
            hideResolveAllCheckbox
          />
        )}
        {selectedTab?.id === "residentialDetails" && (
          <ResidentialDetails
            official={row.original}
            isCurrentUserRow={true}
            viewRestrictedData={true}
            hideResolveAllCheckbox
          />
        )}
        {selectedTab?.id === "uploadDocuments" && (
          <div className="px-8 pt-5 pb-6 mt-6 bg-white">
            <h3 className="mb-2 text-lg">
              Uploaded appointment forms as official
            </h3>
            <div>
              <h4 className="mb-2 font-medium">
                {getFullName(row.original.names)}
              </h4>
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: row.original.official.fileUploads,
                }}
                onSubmit={() => null}
              >
                {() => {
                  return (
                    <div className="pl-4">
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={getDocumentData(
                          row.original.official.idType
                        )}
                        onlyPreview
                        showDocumentLabel
                        hideResolveAllCheckbox
                        resolveAllFieldsCheckbox
                      />
                    </div>
                  );
                }}
              </Formik>
            </div>
          </div>
        )}
        {selectedTab.id === "changedDocuments" && (
          <div className="bg-white py-4 mt-4">
            <div className="flex flex-col py-4 px-2">
              <h3 className="font-semibold text-slate-800">
                Document Selected:
              </h3>
              <div className="flex px-2 py-2 space-x-2">
                <Checkbox
                  name="Signed Documents"
                  label="Signed Documents"
                  checked={
                    row.original?.uploadChangeDocuments?.modeType ===
                    "pre-signed-documents"
                  }
                  disabled
                />
                <Checkbox
                  name="notSIgnedDocuments"
                  label="Not Signed Documents"
                  checked={
                    row.original?.uploadChangeDocuments?.modeType ===
                    "not-signed-documents"
                  }
                  disabled
                />
              </div>
            </div>
            <h3 className="font-semibold text-slate-800 px-2">
              Uploaded File:
            </h3>
            <div className="flex mt-2 justify-between px-6">
              <h2>{row.original.uploadChangeDocuments.fileType}</h2>
              <span
                className="cursor-pointer hover:bg-slate-50"
                onClick={toggleDoc}
              >
                {!toggleViewDoc ? (
                  <div className="flex space-x-2">
                    <EyeIcon className="w-6 h-6" aria-hidden="true" />
                    <span>View details</span>
                  </div>
                ) : (
                  <div className="flex space-x-1">
                    <EyeOffIcon className="w-6 h-6" aria-hidden="true" />
                    <span>Hide details</span>
                  </div>
                )}
              </span>
            </div>
            {toggleViewDoc && <div className="px-2 mt-4">{DocViewer()}</div>}
          </div>
        )}
        {selectedTab.id === "directorApprovalDocuments" && (
          <>
            {row?.original?.uploadDirectorApprovalDocs &&
            row.original.uploadDirectorApprovalDocs.fileName ? (
              <div className="bg-white py-4 mt-4">
                <div className="flex flex-col py-4 px-2">
                  <h3 className="font-semibold text-slate-800">
                    Document Selected:
                  </h3>
                  <div className="flex px-2 py-2 space-x-2">
                    <Checkbox
                      name="Signed Documents"
                      label="Signed Documents"
                      checked={
                        row.original?.uploadDirectorApprovalDocs?.modeType ===
                        "pre-signed-documents"
                      }
                      disabled
                    />
                    <Checkbox
                      name="Not Signed Documents"
                      label="Not Signed Documents"
                      checked={
                        row.original?.uploadDirectorApprovalDocs?.modeType ===
                        "not-signed-documents"
                      }
                      disabled
                    />
                  </div>
                </div>
                <h3 className="font-semibold text-slate-800 px-2">
                  Uploaded File:
                </h3>
                <div className="flex mt-2 justify-between px-6">
                  <h2>Board Resolution</h2>
                  <span
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={toggleDoc}
                  >
                    {!toggleViewDoc ? (
                      <div className="mt-4 flex space-x-2">
                        <EyeIcon className="w-6 h-6" aria-hidden="true" />
                        <span>View details</span>
                      </div>
                    ) : (
                      <div className="flex space-x-1">
                        <EyeOffIcon className="w-6 h-6" aria-hidden="true" />
                        <span>Hide details</span>
                      </div>
                    )}
                  </span>
                </div>
                {toggleViewDoc && (
                  <div className="px-2 mt-4">
                    {DocViewer("directorApprovalDocuments")}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white flex flex-col py-4 mt-4">
                <div className=" mt-4 mb-4 px-6">
                  <div className="rounded-md bg-sky-50 p-4">
                    <div className="flex px-6">
                      <div className="flex-shrink-0">
                        <InformationCircleIcon
                          className="h-5 w-5 text-sky-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3 flex-1 md:flex md:justify-between whitespace-normal">
                        <p className="text-sm text-sky-700">
                          No any documents found! because director approval not
                          yet has been done.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div className="flex justify-between">
          {!isFirstStep && (
            <button
              onClick={goToPrevStep}
              className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
            >
              <span className="flex items-center mr-4 h-7">
                <ChevronLeftIcon
                  className="flex-shrink-0 w-5 h-5"
                  aria-hidden="true"
                />
              </span>
              <p className="font-semibold">Go back</p>
            </button>
          )}
          {isLastStep ? (
            <button
              onClick={() => handleConfirmDetails()}
              className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7"
            >
              <p>Close</p>
            </button>
          ) : (
            <button
              onClick={goToNextStep}
              className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
            >
              <p className="font-semibold">View next step </p>
              <span className="flex items-center ml-4 h-7">
                <ChevronRightIcon
                  className="flex-shrink-0 w-5 h-5"
                  aria-hidden="true"
                />
              </span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default RecentChangesSubRow;
